import { Component, Input, input, InputSignal, model, ModelSignal, output, OutputEmitterRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SwitchComponent } from '@shared/components/ui/switch';
import { ToggleElementDirective, TXPosition, TYPosition } from '@shared/directives';
import { TranslateModule } from '@ngx-translate/core';

type TState = 'default' | 'disabled';

/**
 * Represents a dropdown item with an identifier and description
 * @typedef {Object} TDropdownItem
 * @property {number|string} id
 * @property {string} description
 * @property {boolean} isChecked
 */
export type TDropdownSwitch = {
  id: number | string;
  description: string;
  isChecked: boolean;
  state: TState;
};

/**
 * A reusable dropdown component with configurable options and selection handling
 *
 * @component
 * @selector lib-dropdown-switch
 *
 * @example
 * <lib-dropdown-switch
 * [dataSource]="listOptions"
 * [isDisabled]="false"
 * [show]="showSettingsDropdown"
 * [anchor]="mainAnchor"
 * (switchChange)="onSelectionChange($event)">
 * </lib-dropdown-switch>
 */

@Component({
  selector: 'lib-dropdown-switch',
  imports: [CommonModule, SwitchComponent, ToggleElementDirective, TranslateModule],
  templateUrl: './dropdown-switch.component.html',
  styleUrl: './dropdown-switch.component.css',
})
export class DropdownSwitchComponent {
  /**
     * Array of dropdown items to display
     * @type {InputSignal<TDropdownListItem[]>}
     */
  public readonly dataSource: InputSignal<TDropdownSwitch[]> = input<TDropdownSwitch[]>([]);

  /**
   * Controls whether the dropdown is disabled
   * @type {InputSignal<boolean>}
   */
  public readonly isDisabled: InputSignal<boolean> = input(false);

  /**
   * Controls the visibility of the dropdown (two-way binding)
   * @type {ModelSignal<boolean>}
   */
  public readonly show: ModelSignal<boolean> = model<boolean>(false);

  /**
   * Reference to the anchor element for positioning
   * @type {InputSignal<HTMLElement>}
   */
  public readonly anchor: InputSignal<HTMLElement> = input<HTMLElement>();

  /**
     * @param xAxis The x-axis position of the element.
     * @default 'start'
     */
  public xAxis: InputSignal<TXPosition> = input<TXPosition>('start');

  /**
   * @param yAxis The y-axis position of the element.
   * @default 'end'
   */
  public yAxis: InputSignal<TYPosition> = input<TYPosition>('end');

  /**
   * Emits when a new option is selected
   * @type {OutputEmitterRef<TDropdownListItem>}
   */
  public readonly switchChange: OutputEmitterRef<TDropdownSwitch> = output<TDropdownSwitch>();

  /**
   * Current position of the dropdown relative to anchor
   * @type {string}
   * @default 'bottom'
   */
  public currentPosition = 'bottom';

  /**
   * The currently selected dropdown item
   * @type {TDropdownListItem | null}
   */
  @Input() selectedOption: TDropdownSwitch | null = null;

  /**
 * Emits an event when the list item is clicked, unless disabled.
 * @type {OutputEmitterRef<void>}
 */
  public selectedChange: OutputEmitterRef<void> = output<void>();
  /**
 * Handles the click event on the list item.
 * Emits the `onSelectedChange` event if the item is not disabled.
 * @returns {void}
 */
  public onSwitchChange(option: TDropdownSwitch, isChecked: boolean, state: TState): void {
    if (this.isDisabled()) return;
    option.isChecked = isChecked;
    this.switchChange.emit(option);
  }

}
